import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { tz } from '@date-fns/tz'
import { addDays } from 'date-fns'
import { useAtomValue } from 'jotai'
import _isEmpty from 'lodash/isEmpty'

import eventBus from '@/utils/eventBus'
import { floorMs } from '@/utils/floor-ms'

import type { Cue } from '~shared/rundown/cue'
import { CueStartMode } from '~shared/rundown/cue'
import { applyDate, formatTimeOfDay } from '~shared/timeutils'
import type { Timestamp } from '~shared/utils'
import { CueRunState, fastDeepEqual } from '~shared/utils'

import { momentAtom } from '@/store/moment.store'

import { StartTimeBottomDisplay } from './StartTimeBottomDisplay'
import { StartTimePopover } from './StartTimePopover'
import { StartTimePopoverContent } from './StartTimePopoverContent'
import { StartTimeSubDisplay } from './StartTimeSubDisplay'
import { StartTimeSupDisplay } from './StartTimeSupDisplay'
import { StartTimeTopDisplay } from './StartTimeTopDisplay'
import type { TimingProps, TimingValues } from './timing.types'
import { TimingScaffold } from './TimingScaffold'

type StartTimeProps = {
  cueId: string
  values: TimingValues
  setValues: (update: Partial<TimingValues>) => void
  cellStyle?: CSSProperties
  readonly?: boolean
  loading?: boolean
  timestamp?: Timestamp | null
  timezone: string
  isFirstCue?: boolean
  timingProps: TimingProps
  nextCue: Cue | null
}

/**
 * StartTime Component
 *
 * Pure component for displaying and editing a cue's start time information.
 * Manages internal state but triggers external updates via setValues.
 */
export function StartTime({
  cueId,
  values,
  setValues,
  cellStyle,
  readonly,
  loading,
  timestamp,
  timezone,
  isFirstCue,
  timingProps,
  nextCue,
}: StartTimeProps) {
  const moment = useAtomValue(momentAtom)

  // -----------------------------------------------
  // State Management
  // -----------------------------------------------
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [invalid] = useState<null>(null)
  const [internalValues, setInternalValues] = useState(values)
  const previousValuesRef = useRef(values)
  const isHardStart = useMemo(
    () => internalValues.startMode === CueStartMode.FIXED,
    [internalValues],
  )

  /**
   * Sync internal state when external values change
   * - This implements the "last-action-wins" conflict management
   * - Ignore all upstream changes while popover is open
   */
  useEffect(() => {
    if (isPopoverOpen) return
    const hasExternalChanges = !fastDeepEqual(values, previousValuesRef.current)
    if (hasExternalChanges) {
      setInternalValues(values)
      previousValuesRef.current = values
    }
  }, [values, isPopoverOpen])

  // -----------------------------------------------
  // Popover Handlers
  // -----------------------------------------------
  // Cancel changes and reset to current cue duration
  const handlePopoverCancel = useCallback(() => {
    setInternalValues(values)
  }, [values])

  // Save changes to props
  // Only update if values have actually changed
  const handlePopoverSave = useCallback(
    ({ abort }: { abort?: () => void } = {}) => {
      // Compare values and only submit changed values
      const changedProps: Partial<TimingValues> = {}
      const relevantProps = [
        'startTime',
        'startDatePlus',
        'startMode',
        'scheduled',
      ] as const

      // Only proceed if props are valid
      if (invalid) {
        return abort?.()
      }

      // Check which props changed
      relevantProps.forEach((prop) => {
        if (!fastDeepEqual(internalValues[prop], values[prop])) {
          Object.assign(changedProps, { [prop]: internalValues[prop] })
        }
      })

      // Only update if there are changes
      if (!_isEmpty(changedProps)) {
        setValues(changedProps)
      }
    },
    [internalValues, values, setValues, invalid],
  )

  // -----------------------------------------------
  // Value Change Handlers
  // -----------------------------------------------
  const calcStartTime = useCallback(
    (startTime: Date, startDatePlus: number) => {
      const date = addDays(timingProps.rundownStartTime, startDatePlus, {
        in: tz(timezone),
      })
      return applyDate(startTime, date, { timezone })
    },
    [timingProps, timezone],
  )

  // Save changes to internalValues when properties change
  const onPropChange = useCallback(
    (propName: keyof TimingValues, value: TimingValues[keyof TimingValues]) => {
      const update = { ...internalValues, [propName]: value } as TimingValues

      // Soft cues have startTime=null — initialize from the computed timestamp when switching to FIXED
      if (
        propName === 'startMode' &&
        value === CueStartMode.FIXED &&
        !update.startTime &&
        timestamp?.actual?.start
      ) {
        update.startTime = calcStartTime(
          timestamp.actual.start,
          update.startDatePlus,
        )
      }

      // Handle startDatePlus --> startTime dependency (except for first cue)
      if (
        !isFirstCue &&
        ['startDatePlus', 'startTime'].includes(propName) &&
        update.startTime
      ) {
        update.startTime = calcStartTime(update.startTime, update.startDatePlus)
      }

      setInternalValues(update)
    },
    [calcStartTime, internalValues, isFirstCue, timestamp],
  )

  // -----------------------------------------------
  // Scheduling
  // -----------------------------------------------
  const onScheduleToggle = useCallback(
    (newScheduledValue?: boolean) => {
      setValues({
        scheduled: newScheduledValue ?? !internalValues.scheduled,
      })
    },
    [internalValues, setValues],
  )

  useEffect(() => {
    eventBus.$on(`scheduleToggleCue:${cueId}`, onScheduleToggle)
    return () => eventBus.$off(`scheduleToggleCue:${cueId}`, onScheduleToggle)
  }, [cueId, onScheduleToggle])

  const onScheduleToggleNext = useCallback(() => {
    if (!nextCue) return
    eventBus.$emit(`scheduleToggleCue:${nextCue.id}`)
  }, [nextCue])

  // -----------------------------------------------
  // Visual State Calculations
  // -----------------------------------------------
  // Compute time comparison state for text color
  const timeComparisonState = useMemo(() => {
    if (!timestamp) return 'neutral'

    const originalTime = floorMs(timestamp.original.start)
    const actualTime = floorMs(timestamp.actual.start)

    if (originalTime > actualTime) return 'early'
    if (originalTime < actualTime) return 'late'
    return 'neutral'
  }, [timestamp])

  // Calculate formatted time
  const formattedTime = useMemo(() => {
    if (!timestamp) return ''

    // Always show seconds (00:00:00) for current and future cues to avoid visual countdown jumping.
    let seconds: 'always' | 'nonzero' = 'nonzero'
    if (moment?.running && timestamp.state !== CueRunState.CUE_PAST) {
      if (timestamp.state === CueRunState.CUE_ACTIVE || moment.left < 0)
        seconds = 'always'
    }

    return formatTimeOfDay(timestamp.actual.start, {
      timezone,
      seconds,
      format: timingProps.todDisplayFormat,
    })
  }, [
    moment?.left,
    moment?.running,
    timestamp,
    timezone,
    timingProps.todDisplayFormat,
  ])

  // -----------------------------------------------
  // Rendering
  // -----------------------------------------------
  if (!timestamp) return null

  const timeTextClasses = {
    neutral: '',
    early: 'text-green-600',
    late: 'text-red-600',
  }

  return (
    <TimingScaffold
      style={cellStyle}
      loading={loading}
      sup={
        <StartTimeSupDisplay
          startTime={internalValues.startTime}
          startMode={internalValues.startMode}
          scheduled={internalValues.scheduled}
          setScheduled={onScheduleToggle}
          readonly={readonly || moment?.cueId === cueId}
          timezone={timezone}
          todDisplayFormat={timingProps.todDisplayFormat}
        />
      }
      top={
        <StartTimeTopDisplay
          timestamp={timestamp}
          timezone={timezone}
          todDisplayFormat={timingProps.todDisplayFormat}
          cellStyle={cellStyle}
        />
      }
      bottom={<StartTimeBottomDisplay daysPlus={timestamp?.actual?.daysPlus} />}
      sub={
        <StartTimeSubDisplay
          active={Boolean(
            nextCue &&
            nextCue.scheduled &&
            nextCue.startMode === CueStartMode.FLEXIBLE,
          )}
          onToggle={onScheduleToggleNext}
          readonly={
            readonly ||
            !nextCue ||
            moment?.cueId === nextCue.id ||
            nextCue.startMode !== CueStartMode.FLEXIBLE
          }
        />
      }
    >
      <StartTimePopover
        open={isPopoverOpen}
        setOpen={setIsPopoverOpen}
        isHardStart={isHardStart}
        readonly={readonly}
        onSave={handlePopoverSave}
        onCancel={handlePopoverCancel}
        formattedTime={formattedTime}
        timeClassName={timeTextClasses[timeComparisonState]}
      >
        <StartTimePopoverContent
          startTime={internalValues.startTime}
          startDatePlus={internalValues.startDatePlus}
          startMode={internalValues.startMode}
          scheduled={internalValues.scheduled}
          setStartTime={onPropChange.bind(null, 'startTime')}
          setStartDatePlus={onPropChange.bind(null, 'startDatePlus')}
          setStartMode={onPropChange.bind(null, 'startMode')}
          setScheduled={onPropChange.bind(null, 'scheduled')}
          disabled={readonly || timestamp?.state === CueRunState.CUE_ACTIVE}
          timezone={timezone}
          timestamp={timestamp}
          isFirstCue={isFirstCue}
          timingProps={timingProps}
        />
      </StartTimePopover>
    </TimingScaffold>
  )
}
